import {
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { makeStyles } from 'tss-react/mui';
import React, { memo } from 'react';
import Colors from '../../../utils/color';
import { HITL3_FONT_SIZE } from '../../../utils/hitl3Typography';
import { useAppSelector } from '../../../app/hooks';
import { selectFeatureFlagHitl3 } from '../../../redux/features/config/config.selector';

export interface IModChoiceStyleProps {
  is86edToday: boolean;
  isDefault: boolean;
  // hitl_3_0 ON: lighter chip label matching the "Welcome" intent pill (shared
  // hitl3 font size / weight 500) instead of the legacy 12px / bold.
  isHitl3: boolean;
}

const useStyles = makeStyles<IModChoiceStyleProps>()(
  ({ spacing }, { is86edToday, isDefault, isHitl3 }) => {
    const chipColor = is86edToday ? Colors.red : '';
    const chipTextDecoration = is86edToday ? 'line-through' : '';
    return {
      button: {
        display: 'none !important',
      },
      chipContainer: {
        borderRadius: `${spacing(0.5)} !important`,
        // hitl_3_0: match the "Welcome" intent pill (weight 500); legacy stays bold.
        fontWeight: isHitl3 ? '500 !important' : 'bold !important',
        textTransform: 'uppercase',
        height: `${spacing(5)} !important`,
        width: '100%',
        '& .MuiChip-label': {
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          textAlign: 'center',
          padding: '0px 8px !important',
          whiteSpace: 'normal',
        },
      },
      controlLabel: {
        width: '18%',
        margin: `0px ${spacing(1)} ${spacing(1)} 0px !important`,
        height: `${spacing(5)}`,
        display: 'inline-block !important',
        '& .MuiFormControlLabel-label': {
          display: 'inline-block !important',
          width: '100%',
        },
        '& .MuiChip-root.MuiChip-filled': {
          backgroundColor: Colors.mineShaft,
          color: Colors.white,
        },
        '& .MuiChip-root': {
          cursor: is86edToday ? 'default' : 'pointer',
          borderStyle: isDefault ? 'dashed' : 'solid',
          borderWidth: isDefault ? '2px' : '1px',
          // hitl_3_0: shared hitl3 size; legacy 12px (spacing(1.5)) unaffected.
          fontSize: isHitl3 ? HITL3_FONT_SIZE : spacing(1.5),
          color: chipColor,
          textDecoration: chipTextDecoration,
        },
      },
    };
  }
);

interface IMultipleModChoice {
  id: string;
  name: string;
  modLabel: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  is86edToday: boolean;
  isSelected: boolean;
  isDefault: boolean;
  onInfoClick?: (event: React.MouseEvent) => void;
  description?: string;
}

const MultipleModChoice = ({
  id,
  name,
  modLabel,
  onChange,
  is86edToday,
  isSelected,
  isDefault,
  onInfoClick,
  description,
}: IMultipleModChoice) => {
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const { classes } = useStyles({ is86edToday, isDefault, isHitl3 });

  const ctrl = (
    <Checkbox
      checked={isSelected}
      color="primary"
      value={id}
      onChange={onChange}
      name={id}
      className={classes.button}
      disabled={is86edToday}
    />
  );
  const label = (
    <div style={{ position: 'relative', width: '100%' }}>
      <Chip
        size="small"
        title={name}
        label={modLabel}
        variant={isSelected ? 'filled' : 'outlined'}
        className={classes.chipContainer}
      />
      {onInfoClick && (
        <Tooltip title={description || ''} arrow placement="top">
          <IconButton
            size="small"
            onClick={(event) => {
              // Stop propagation here so the icon-click never bubbles to the
              // FormControlLabel and toggles selection — the contract lives
              // with the click target, not each caller.
              event.stopPropagation();
              onInfoClick(event);
            }}
            aria-label={`Play description for ${name}`}
            sx={{
              position: 'absolute',
              right: -6,
              top: -6,
              padding: 0,
              backgroundColor: 'rgba(255,255,255,0.85)',
              color: 'inherit',
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );

  return (
    <FormControlLabel
      control={ctrl}
      label={label}
      className={classes.controlLabel}
    />
  );
};

export default memo(MultipleModChoice);
